import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import {
  useFristenUebersicht, useKuendigungen, useKuendigungZuordnen, useKuendigungBestaetigen,
  useVertraege, datum, eur,
} from "../hooks/useVertraege";
import type { FristenEintrag, Kuendigung } from "../hooks/useVertraege";

/**
 * Fristen — was ausläuft, was widerrufen werden kann, was gekündigt wurde.
 *
 * Die Seite existiert, weil die Kündigungsschaltfläche (§ 312k BGB) seit dem
 * 12.08.2026 Erklärungen entgegennimmt. Eine Kündigung, die niemand bearbeitet,
 * läuft trotzdem — sie ist mit dem Zugang wirksam. Was hier steht, sind also
 * keine Erinnerungen, sondern Termine, die ohne Zutun eintreten.
 *
 * Seit 09.10.2026 kein Eintrag in der Leiste mehr: jede Kündigung und jeder
 * nahe Kündigungstermin steht als Aufgabe unter „Aufgaben" und führt hierher.
 * Die Preisrunde ist in die Auswertung umgezogen.
 */
export default function FristenPage() {
  const { data: u, isLoading } = useFristenUebersicht();
  const { data: kuendigungen = [] } = useKuendigungen(true);

  return (
    <Shell
      title="Kündigungen und Fristen"
      subtitle={u ? `Stand ${datum(u.stichtag)} · nächste 90 Tage` : ""}
      action={<Link className="pf-btn is-ghost" to="/auswertung#preisrunde">Preisrunde</Link>}
    >
      {isLoading && <div className="pf-empty">Lade…</div>}

      {kuendigungen.length > 0 && (
        <>
          <div className="pf-seclabel">
            Kündigungen — {kuendigungen.length} unbearbeitet
          </div>
          {kuendigungen.map(k => <KuendigungsKarte key={k.id} k={k} />)}
        </>
      )}

      <Gruppe
        titel="Laufen aus"
        leer="In den nächsten 90 Tagen läuft keine Kündigungsfrist ab."
        eintraege={u?.laufen_aus || []}
        zeile={e => `Kündigungstermin ${datum(e.fristen.naechster_kuendigungstermin)} · noch ${e.fristen.tage_bis_kuendigungstermin} Tage`}
      />
      <Gruppe
        titel="In der Widerrufsfrist"
        leer="Kein Vertrag ist derzeit widerruflich."
        eintraege={u?.in_widerrufsfrist || []}
        zeile={e => `Widerruf möglich bis ${datum(e.fristen.widerruf_ablauf)}`}
      />
    </Shell>
  );
}

function Gruppe({ titel, leer, eintraege, zeile }: {
  titel: string; leer: string; eintraege: FristenEintrag[];
  zeile: (e: FristenEintrag) => string;
}) {
  const navigate = useNavigate();
  return (
    <>
      <div className="pf-seclabel">{titel}{eintraege.length ? ` — ${eintraege.length}` : ""}</div>
      {eintraege.length === 0 ? (
        <div className="pf-empty" style={{ marginBottom: 14 }}>{leer}</div>
      ) : eintraege.map(e => (
        <button key={e.id} className="pf-row" onClick={() => navigate(`/vertraege/${e.id}`)}>
          <div className="pf-row-main" style={{ textAlign: "left" }}>
            <div style={{ fontWeight: 600 }}>{e.kunde_name}</div>
            <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)" }}>
              {e.nummer} · {zeile(e)}
            </div>
          </div>
          <div style={{ fontSize: 13, color: "var(--pf-ink-3)" }}>{eur(e.jahres_netto)}</div>
        </button>
      ))}
    </>
  );
}

/**
 * Eine eingegangene Kündigung.
 *
 * Zwei Schritte, bewusst getrennt: **zuordnen** (welcher Vertrag ist gemeint)
 * und **bestätigen** (wann endet er). Der Termin kommt aus dem Fristenrechner
 * und steht mit Begründung da — er lässt sich überschreiben, etwa bei einer
 * einvernehmlich früheren Beendigung, aber nicht versehentlich.
 */
function KuendigungsKarte({ k }: { k: Kuendigung }) {
  const { data: vertraege = [] } = useVertraege();
  const zuordnen = useKuendigungZuordnen();
  const bestaetigen = useKuendigungBestaetigen();
  const [wahl, setWahl] = useState("");
  const [abweichend, setAbweichend] = useState("");
  const [bemerkung, setBemerkung] = useState("");
  const [meldung, setMeldung] = useState("");
  const [fehler, setFehler] = useState("");

  const kandidaten = vertraege.filter(
    v => !["entwurf", "beendet", "gekuendigt"].includes(v.status),
  );

  async function lauf(fn: () => Promise<unknown>, text: string) {
    setFehler(""); setMeldung("");
    try { await fn(); setMeldung(text); }
    catch (e) { setFehler(e instanceof Error ? e.message : "Fehlgeschlagen."); }
  }

  return (
    <div className="pf-card" style={{ marginBottom: 12, borderLeft: "3px solid var(--pf-red)" }}>
      <div style={{ fontWeight: 700 }}>
        {k.erklaerer_name || "ohne Namen"}
        <span style={{ fontWeight: 400, color: "var(--pf-ink-3)" }}>
          {" "}· {k.art === "ausserordentlich" ? "außerordentlich" : "ordentlich"}
        </span>
      </div>
      <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 2 }}>
        Eingang {datum(k.eingang_am)} · {k.kanal === "schaltflaeche" ? "Kündigungsschaltfläche" : k.kanal}
        {k.kennung ? ` · angegeben: ${k.kennung}` : ""}
        {k.erklaerer_kontakt ? ` · ${k.erklaerer_kontakt}` : ""}
      </div>

      {k.vertrag_id ? (
        <>
          <div style={{ fontSize: 13.5, marginTop: 10 }}>
            Zugeordnet: <strong>{k.nummer}</strong> — {k.kunde_name}
          </div>
          <div className="pf-card" style={{ marginTop: 10, background: "var(--pf-surface-2, transparent)" }}>
            <div style={{ fontWeight: 700, fontSize: 13.5 }}>
              Vertragsende: {datum(k.vorschlag.wirksam_zum)}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 4, lineHeight: 1.5 }}>
              {k.vorschlag.begruendung}
            </div>
          </div>
          <div className="pf-grid-2" style={{ marginTop: 10 }}>
            <label className="pf-field"><span>Abweichendes Ende (optional)</span>
              <input className="pf-input" type="date" value={abweichend}
                onChange={e => setAbweichend(e.target.value)} /></label>
            <label className="pf-field"><span>Bemerkung</span>
              <input className="pf-input" value={bemerkung} placeholder="z. B. telefonisch bestätigt"
                onChange={e => setBemerkung(e.target.value)} /></label>
          </div>
          <button className="pf-btn" style={{ marginTop: 10 }} disabled={bestaetigen.isPending}
            onClick={() => lauf(
              () => bestaetigen.mutateAsync({ kid: k.id, wirksam_zum: abweichend || null, bemerkung }),
              "Kündigung bestätigt, Vertrag beendet, Kunde benachrichtigt.",
            )}>
            {bestaetigen.isPending ? "Bestätige…" : "Kündigung bestätigen und Kunden benachrichtigen"}
          </button>
        </>
      ) : (
        <>
          <div style={{ fontSize: 13, marginTop: 10, color: "var(--pf-ink-2)" }}>
            Noch keinem Vertrag zugeordnet — die Angabe im Formular hat auf keinen
            laufenden Vertrag gepasst.
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <select className="pf-input" style={{ flex: "1 1 220px" }} value={wahl}
              onChange={e => setWahl(e.target.value)}>
              <option value="">Vertrag wählen…</option>
              {kandidaten.map(v => (
                <option key={v.id} value={v.id}>{v.kunde_name} — {v.nummer}</option>
              ))}
            </select>
            <button className="pf-btn is-ghost" disabled={!wahl || zuordnen.isPending}
              onClick={() => lauf(
                () => zuordnen.mutateAsync({ kid: k.id, vertrag_id: wahl }),
                "Zugeordnet — jetzt das Vertragsende bestätigen.",
              )}>
              Zuordnen
            </button>
          </div>
        </>
      )}

      {meldung && <div style={{ fontSize: 12.5, marginTop: 8, color: "var(--pf-ink-3)" }}>{meldung}</div>}
      {fehler && <div style={{ fontSize: 12.5, marginTop: 8, color: "var(--pf-red)" }}>{fehler}</div>}
    </div>
  );
}
